import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api, errMsg } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Field, Stamp } from "@/components/common";
import { toast } from "sonner";

export default function Login() {
  const { login, user } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [demo, setDemo] = useState([]);

  useEffect(() => { api.get("/auth/demo-accounts").then((r) => setDemo(r.data)).catch(() => {}); }, []);
  useEffect(() => { if (user) nav("/dashboard"); }, [user, nav]);

  const submit = async (e, em = email, pw = password) => {
    e?.preventDefault();
    setBusy(true); setError("");
    try { await login(em, pw); toast.success("Welcome back"); } catch (err) { setError(errMsg(err)); }
    setBusy(false);
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-[1.1fr_1fr]">
      <div className="hidden lg:flex flex-col justify-between p-12 bg-[var(--paper)] border-r border-border relative overflow-hidden">
        <div className="flex items-center gap-2">
          <img src="/logo.png" alt="Avocado HRMS" className="w-11 h-11 object-contain" data-testid="login-logo" />
          <span className="font-serif text-2xl font-semibold tracking-tight">Avocado <span className="text-primary">HRMS</span></span>
        </div>
        <div className="max-w-lg rise">
          <div className="eyebrow mb-4">Vol. 1 · The People Ledger</div>
          <h1 className="font-serif text-5xl xl:text-6xl leading-[1.02] tracking-tight font-medium">An HR system that reads like a well-kept journal.</h1>
          <p className="mt-6 text-base text-muted-foreground leading-relaxed">Attendance, leave, payroll and people — stamped, dated and filed in Bikram Sambat and AD. Built for Nepal: SSF, CIT, Dashain bonus and the IRD tax slabs, ready out of the box.</p>
          <div className="mt-8 flex gap-3 items-center"><Stamp status="approved" animate /><Stamp status="pending" /><Stamp status="rejected" /><span className="text-xs text-muted-foreground font-mono">approvals, the old-fashioned way</span></div>
        </div>
        <img src="https://images.unsplash.com/photo-1544735716-392fe2489ffa?crop=entropy&cs=srgb&fm=jpg&q=80&w=1200" alt="Himalaya, Nepal" className="absolute -right-24 -bottom-16 w-[520px] h-[360px] object-cover rounded-2xl rotate-[-4deg] opacity-90 shadow-2xl ring-8 ring-card" />
      </div>

      <div className="flex items-center justify-center p-6 sm:p-12">
        <div className="w-full max-w-md space-y-8 rise">
          <div>
            <div className="eyebrow mb-2">Sign in</div>
            <h2 className="font-serif text-3xl font-medium tracking-tight">Open your ledger</h2>
          </div>
          <form onSubmit={submit} className="space-y-4" data-testid="login-form">
            <Field label="Work email"><Input data-testid="login-email-input" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@company.com" required className="h-11" /></Field>
            <Field label="Password"><Input data-testid="login-password-input" type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="••••••••" required className="h-11" /></Field>
            {error && <div data-testid="login-error" className="text-sm text-destructive bg-destructive/10 border border-destructive/30 rounded-md px-3 py-2">{error}</div>}
            <Button data-testid="login-submit-button" type="submit" disabled={busy} className="w-full h-11 rounded-full text-base">{busy ? "Opening…" : "Sign in"}</Button>
          </form>
          {demo.length > 0 && (
            <div className="paper-card p-4" data-testid="demo-accounts">
              <div className="eyebrow !text-muted-foreground mb-3">Try a role · one click</div>
              <div className="grid grid-cols-2 gap-2">
                {demo.map((d) => (
                  <button key={d.email} data-testid={`demo-login-${d.role}`} onClick={(e) => submit(e, d.email, d.password)}
                    className="text-left rounded-lg border border-border px-3 py-2 hover:border-primary/60 hover:bg-secondary/50 transition-colors">
                    <div className="text-sm font-medium">{d.role_label}</div>
                    <div className="text-[11px] text-muted-foreground truncate">{d.name}</div>
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
